import React from 'react';
import { Box, Typography, Card, CardContent, Grid, Avatar } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { BRAND_COLORS, alpha } from '../../../styles/theme';
import { PersonnelMember } from '../../../types/api';
import { useTranslation } from '../../../i18n';
import { ROLE_CONFIG } from './overviewConfig';

// One reusable personnel grid (STD-014 split out of OverviewTab), parameterized
// by `role` — replaces the two near-identical pilots / maintenance-engineers
// blocks. Colors come from ROLE_CONFIG; the heading text and empty-state copy
// are supplied by the parent via i18n keys so each role keeps its own labels.

interface PersonnelGridProps {
  // Key into ROLE_CONFIG ('pilot' | 'maintenance') — drives the accent color.
  role: keyof typeof ROLE_CONFIG;
  people: PersonnelMember[];
  // i18n key for the section heading (count is appended by this component).
  headingKey: Parameters<ReturnType<typeof useTranslation>['t']>[0];
  // i18n key for the empty-state message.
  emptyKey: Parameters<ReturnType<typeof useTranslation>['t']>[0];
  onPersonClick: (personId: string) => void;
}

export const PersonnelGrid: React.FC<PersonnelGridProps> = ({
  role,
  people,
  headingKey,
  emptyKey,
  onPersonClick,
}) => {
  const { t } = useTranslation();
  const roleConfig = ROLE_CONFIG[role];

  return (
    <>
      <Box sx={{ mt: role === 'pilot' ? 3 : 4, mb: 2 }}>
        <Typography variant="h5" sx={{ color: roleConfig.color }}>
          {t(headingKey)} ({people.length})
        </Typography>
      </Box>

      {people.length > 0 ? (
        <Grid container spacing={1.5}>
          {people.map((person) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={person.id}>
              <Card
                onClick={() => onPersonClick(person.id)}
                sx={{
                  border: `1px solid ${roleConfig.color}`,
                  cursor: 'pointer',
                  position: 'relative',
                  height: '100px',
                  backgroundColor: roleConfig.bgColor,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out',
                    boxShadow: `0 4px 8px ${alpha(roleConfig.color, 0.2)}`,
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: '12px !important',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: roleConfig.color,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: BRAND_COLORS.forestGreen.main,
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {person.full_name || t('overview.personnel.unnamed')}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t(emptyKey)}
          </Typography>
        </Box>
      )}
    </>
  );
};
